import { Trans } from "react-i18next";
import React from "react";
import { Alert, Button } from "react-bootstrap";
import { useAuth } from "@shared/context/AuthContext";
import { useRoleLookupConfig } from "@shared/hooks/useLookupConfig";
import { isAdminLikeRole } from "@shared/utils/roleRegistry";
import {
  FeatureAccessDeniedReason,
  getFeatureAccessPresentation,
} from "@shared/hooks/useFeatureFlags";

interface FeatureAccessAlertProps {
  reason?: FeatureAccessDeniedReason;
  detail?: string;
  onRetry?: () => void;
  userRole?: string;
}

const FeatureAccessAlert: React.FC<FeatureAccessAlertProps> = ({
  reason,
  detail,
  onRetry,
  userRole,
}) => {
  const { user } = useAuth();
  const roleLookups = useRoleLookupConfig();
  const presentation = getFeatureAccessPresentation({ reason, detail });
  const effectiveRole = userRole || user?.role;
  const adminView = isAdminLikeRole(effectiveRole, roleLookups);
  const primaryActionLabel =
    adminView || !reason
      ? presentation.primaryActionLabel
      : reason === "ORG_USER_NOT_ASSIGNED_SEAT"
        ? "Contact your administrator"
        : "Contact Administrator";
  const primaryActionHref = adminView ? presentation.primaryActionHref : undefined;

  return (
    <Alert variant={presentation.variant}>
      <Alert.Heading>{presentation.title}</Alert.Heading>
      <p className="mb-2">{presentation.message}</p>
      <div className="d-flex gap-2">
        {primaryActionLabel && primaryActionHref ? (
          <Button as="a" href={primaryActionHref} variant="primary" size="sm">
            {primaryActionLabel}
          </Button>
        ) : null}
        {primaryActionLabel && !primaryActionHref ? (
          <Button variant="outline-secondary" size="sm" disabled>
            {primaryActionLabel}
          </Button>
        ) : null}
        {onRetry ? (
          <Button
            variant={
              presentation.variant === "info"
                ? "outline-info"
                : presentation.variant === "secondary"
                  ? "outline-secondary"
                  : "outline-warning"
            }
            size="sm"
            onClick={onRetry}
          >
            <Trans ns="features" i18nKey="ui.copy.bc02dacf3756" /></Button>
        ) : null}
      </div>
    </Alert>
  );
};

export default FeatureAccessAlert;
